'use client';

import React, { useState, useEffect } from 'react';

type ConsentValue = 'granted' | 'denied';

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? decodeURIComponent(match[3]) : null;
}

function setCookie(name: string, value: string, days = 365) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function applyConsent(value: ConsentValue) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  const consentParams = {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  };

  // Google Tag Manager Consent Mode update
  if (typeof (window as any).gtag === 'function') {
    (window as any).gtag('consent', 'update', consentParams);
  } else {
    (window.dataLayer as any[]).push({ event: 'consent_update', ...consentParams });
  }

  // CookieHub consent event dispatch (matching Planhat dataLayer listener)
  (window.dataLayer as any[]).push({
    event: 'cookiehub_consent_update',
    consent: value,
    timestamp: Date.now(),
  });
  // Planhat GTM listener uses typo 'cookiehbu_consent_update'
  (window.dataLayer as any[]).push({
    event: 'cookiehbu_consent_update',
    consent: value,
    timestamp: Date.now(),
  });
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(true);

  useEffect(() => {
    // Expose CookieHub API window.cookiehub.openSettings()
    (window as any).cookiehub = {
      openSettings: () => setShowSettings(true),
      hasConsented: () => !!getCookie('cookiehub'),
    };

    // Mode Cookie: determine visibility strictly from document.cookie
    const cookiehubCookie = getCookie('cookiehub');
    const consentCookie = getCookie('cookie_consent') as ConsentValue | null;

    if (!cookiehubCookie && !consentCookie) {
      setVisible(true);
    } else {
      const consent = consentCookie || (cookiehubCookie?.includes('"analytics":true') ? 'granted' : 'denied');
      applyConsent(consent as ConsentValue);
    }
  }, []);

  function acceptAll() {
    setCookie(
      'cookiehub',
      JSON.stringify({ analytics: true, marketing: true, functional: true, timestamp: Date.now() }),
      365
    );
    setCookie('cookie_consent', 'granted', 365);
    applyConsent('granted');
    setVisible(false);
    setShowSettings(false);
  }

  function declineAll() {
    setCookie(
      'cookiehub',
      JSON.stringify({ analytics: false, marketing: false, functional: false, timestamp: Date.now() }),
      365
    );
    setCookie('cookie_consent', 'denied', 365);
    applyConsent('denied');
    setVisible(false);
    setShowSettings(false);
  }

  function saveCustomPreferences() {
    const value: ConsentValue = analyticsConsent || marketingConsent ? 'granted' : 'denied';
    setCookie(
      'cookiehub',
      JSON.stringify({
        analytics: analyticsConsent,
        marketing: marketingConsent,
        functional: true,
        timestamp: Date.now(),
      }),
      365
    );
    setCookie('cookie_consent', value, 365);
    applyConsent(value);
    setVisible(false);
    setShowSettings(false);
  }

  return (
    <>
      {visible && (
        <div
          id="ch2-dialog"
          className="ch2-dialog ch2-dialog-bottom ch2-visible fixed bottom-0 left-0 right-0 z-50 w-full bg-white text-black border-t border-black/10 shadow-md"
          role="dialog"
          tabIndex={-1}
          aria-label="Cookie consent"
          aria-describedby="ch2-dialog-description"
        >
          <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-3 sm:py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="ch2-dialog-content text-[13px] sm:text-sm leading-relaxed">
              <p id="ch2-dialog-description" className="inline text-black/75">
                We use cookies to improve your experience and for analytics. Learn more in our{' '}
              </p>
              <p className="inline">
                <a
                  href="/privacy"
                  rel="noopener"
                  target="_blank"
                  className="ch2-learn-more underline underline-offset-2 font-medium hover:opacity-70 transition-opacity"
                  tabIndex={0}
                >
                  Cookie Policy.
                </a>
              </p>
            </div>
            <div className="ch2-dialog-actions flex items-center gap-3 shrink-0">
              <button
                type="button"
                tabIndex={0}
                onClick={() => setShowSettings(true)}
                className="ch2-btn ch2-open-settings-btn ch2-btn-text px-3 py-2 text-xs font-mono uppercase tracking-wider text-black/60 hover:text-black transition-colors cursor-pointer"
              >
                Manage Preferences
              </button>
              <button
                type="button"
                tabIndex={0}
                onClick={acceptAll}
                className="ch2-btn ch2-allow-all-btn ch2-btn-primary px-4 py-2 rounded-none bg-black text-white hover:bg-black text-xs font-mono uppercase tracking-wider transition-all cursor-pointer"
              >
                Accept Cookies
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal (Manage Preferences) */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] bg-black/45 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white text-black max-w-md w-full rounded-[2px] border border-black/15 shadow-2xl p-6 sm:p-8 space-y-6">
            <div className="flex items-center justify-between pb-3 border-b border-black/10">
              <h3 className="font-semibold text-base tracking-tight font-sans">Cookie Preferences</h3>
              <span className="text-[11px] font-mono text-black/50 uppercase tracking-widest">Minimoes</span>
            </div>

            <div className="space-y-4 text-xs font-sans">
              <div className="flex items-center justify-between p-3 rounded-[1px] bg-neutral-50 border border-black/5">
                <div>
                  <div className="font-semibold text-black">Essential Cookies</div>
                  <div className="text-black/60 text-[11px] mt-0.5">Required for core system security and session routing.</div>
                </div>
                <span className="text-[11px] font-mono uppercase text-black/40">Always Active</span>
              </div>

              <div className="flex items-center justify-between p-3 rounded-[1px] border border-black/10">
                <div>
                  <div className="font-semibold text-black">Analytics &amp; Performance</div>
                  <div className="text-black/60 text-[11px] mt-0.5">Measures interaction metrics and platform diagnostics.</div>
                </div>
                <input
                  type="checkbox"
                  checked={analyticsConsent}
                  onChange={(e) => setAnalyticsConsent(e.target.checked)}
                  className="w-4 h-4 accent-black cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-[1px] border border-black/10">
                <div>
                  <div className="font-semibold text-black">Marketing &amp; Personalization</div>
                  <div className="text-black/60 text-[11px] mt-0.5">Customizes content and campaign attribution telemetry.</div>
                </div>
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="w-4 h-4 accent-black cursor-pointer"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-4 border-t border-black/10 font-mono text-xs uppercase">
              <button
                type="button"
                onClick={declineAll}
                className="px-3 py-2 text-black/60 hover:text-black transition-colors cursor-pointer"
              >
                Reject All
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveCustomPreferences}
                  className="px-3.5 py-2 border border-black/20 hover:border-black rounded-none text-black bg-white transition-all cursor-pointer"
                >
                  Save Choices
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="px-4 py-2 bg-black text-white hover:bg-black rounded-none transition-all cursor-pointer"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
